import {gql} from '@apollo/client';
import React, {useCallback, useMemo, useEffect} from 'react';

import {assert} from 'Asserts';
import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  Worksheet,
  WorksheetViewStateAction,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import WorksheetDetails from 'notebook/worksheets/workspace/WorksheetDetails';
import WorksheetPrinterFriendly from 'notebook/worksheets/workspace/WorksheetPrinterFriendly';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  WORKSHEET_INITIAL_LOAD_ENDED_EVENT,
  composeWorksheetInitialLoadEventMetadata,
} from 'performance/processWorksheetsEvents';
import {PMEIsQuery, PMEIsQueryVariables} from 'procedures/worksheets/__generated/PMEIsQuery';
import {ProcedureWorksheetContext} from 'procedures/worksheets/ProcedureWorksheetContext';
import {getProcedureType} from 'procedures/worksheets/WorksheetUtils';
import {CenteredSpinner} from 'ui-library/Spinner';

export enum WorksheetProcedureLoadingState {
  NOT_LOADED,
  LOADING,
  LOADED,
}

export interface ProcedureWorksheetConnectorProps {
  viewer?: boolean;
  worksheet: Worksheet;
  refetch: (isServerUpdate?: boolean) => Promise<void>;
  onSetWorksheet?: (id: string) => boolean;
  isExecutionEnabled: boolean;
  dispatch: (action: WorksheetViewStateAction) => void;
  loadingState: WorksheetProcedureLoadingState;
  setLoadingState: (loadingState: WorksheetProcedureLoadingState) => void;
  worksheetViewState: WorksheetViewStateType;
}

export const PMEIS_QUERY = gql`
  # pmei.methodTask.statusChanges does not have ID field
  # eslint-disable benchlint/id-field-required
  query PMEIsQuery($worksheetId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    procedureMethodExecutionInstances(worksheetId: $worksheetId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        archiveData {
          archived
        }
        procedureRun {
          ... on ProcedureRunNode {
            id
            procedureType
            defaultDataEntryMode
            study {
              ... on StudyNode {
                id
                studyDesign {
                  id
                  type
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
        taskTransitionExtensions {
          statusDisplayName
          timestampUtcIsoformat
        }
        procedureRunConditionReplicate {
          id
          replicateNumber
          procedureRunCondition {
            id
            name
          }
        }
        planMethodDefinitionVersion {
          ... on BaseProcedureMethodDefinitionVersionInterface {
            id
            name
            materialOutputSettings {
              version {
                id
                name
              }
            }
            parameterUsagesExpensive {
              parameterVersionId
              stepId
              valueType
            }
          }
        }
        materialOutputConfirmationStep {
          id
          name
          document {
            ... on DocumentNode {
              id
              editUrl
            }
            ...InaccessibleInfoGraphQL
          }
        }
        materialOutputConfirmationValues {
          materialOutputVersion {
            id
            name
          }
          material
        }
        methodTask {
          ... on ProcessTaskNode {
            id
            statusChanges {
              ... on ProcessTaskStatusChangeNode {
                creator {
                  ...PartySummaryGraphQL
                }
                createdAt
                newStatus {
                  ... on ProcessTaskStatusNode {
                    id
                    displayName
                    statusType
                  }
                }
              }
            }
            status {
              ... on ProcessTaskStatusNode {
                id
                displayName
                statusType
              }
            }
            taskSchema {
              ... on ProcessTaskSchemaNode {
                id
                statusLifecycle {
                  ... on ProcessTaskStatusLifecycleNode {
                    id
                    transitions {
                      id
                      outStatus {
                        ... on ProcessTaskStatusNode {
                          id
                          displayName
                        }
                      }
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            lastStatusChange {
              ... on ProcessTaskStatusChangeNode {
                creator {
                  ...PartySummaryGraphQL
                }
                createdAt
              }
            }
          }

          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        previousProcedureMethodExecutionInstances {
          ... on ProcedureMethodExecutionInstanceNode {
            id
            methodTask {
              ... on ProcessTaskNode {
                id
                status {
                  ... on ProcessTaskStatusNode {
                    id
                    displayName
                  }
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PartySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcedureWorksheetConnector(props: ProcedureWorksheetConnectorProps): JSX.Element {
  const {refetch: refetchWorksheet, viewer, onSetWorksheet, worksheet, loadingState, setLoadingState} = props;
  const resultPMEIs = useBenchlingQuery<PMEIsQuery, PMEIsQueryVariables>(PMEIS_QUERY, {
    variables: {worksheetId: props.worksheet.id},
    renderCachedDataWhenRefetching: true,
  });
  const refetchPMEIs = useCallback(async (): Promise<void> => {
    await resultPMEIs.refetch({worksheetId: props.worksheet.id});
  }, [resultPMEIs, props.worksheet.id]);
  const refetchWorksheetAndPMEIs = useCallback(
    async (isServerUpdate?: boolean): Promise<void> => {
      await Promise.all([refetchWorksheet(isServerUpdate), refetchPMEIs()]);
    },
    [refetchWorksheet, refetchPMEIs]
  );

  const procedureWorksheetContext = useMemo(() => {
    const procedureType =
      resultPMEIs.type === QueryResultType.SUCCESS
        ? getProcedureType(resultPMEIs.data.procedureMethodExecutionInstances)
        : undefined;
    // If this is an assay type procedure, check if the assay feature is enabled
    // If this is a recipe type procedure, check if the recipe feature is enabled
    // If the procedure type is not defined, return true until we can determine the procedure type
    const isProcedureFeatureEnabled = procedureType
      ? procedureType === ProcedureType.ASSAY
        ? isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN})
        : isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN})
      : true;
    return {
      PMEIs:
        resultPMEIs.type === QueryResultType.SUCCESS
          ? resultPMEIs.data.procedureMethodExecutionInstances
          : [],
      refetchPMEIs,
      isProcedureFeatureEnabled,
    };
  }, [refetchPMEIs, resultPMEIs]);
  useEffect(() => {
    if (
      resultPMEIs.type === QueryResultType.SUCCESS &&
      loadingState === WorksheetProcedureLoadingState.LOADING
    ) {
      logProfilerTimingEvent(
        WORKSHEET_INITIAL_LOAD_ENDED_EVENT,
        composeWorksheetInitialLoadEventMetadata(
          worksheet,
          BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION,
          getProcedureType(resultPMEIs.data.procedureMethodExecutionInstances)
        )
      );
      setLoadingState(WorksheetProcedureLoadingState.LOADED);
    }
  });
  if (resultPMEIs.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  } else {
    assert(Boolean(onSetWorksheet !== undefined || viewer), 'onSetWorksheet must be provided');
    return (
      <ProcedureWorksheetContext.Provider value={procedureWorksheetContext}>
        {viewer ? (
          <WorksheetPrinterFriendly worksheet={props.worksheet} />
        ) : (
          <WorksheetDetails {...props} refetch={refetchWorksheetAndPMEIs} onSetWorksheet={onSetWorksheet!} />
        )}
      </ProcedureWorksheetContext.Provider>
    );
  }
}
